import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ErrorEvent,
  ErrorEventSummary,
  ErrorIssue,
  ErrorIssueDetail,
  ErrorIssueFilters,
  ErrorIssueStatus,
  ErrorTrackingKey,
  ErrorTrackingStats,
  Paginated,
  SourceMapArtifact,
} from '../models/error-tracking.model';
import { UrlConstantsService } from './url-constants.service';

@Injectable()
export class ErrorTrackingService {
  constructor(
    private _http: HttpClient,
    private urls: UrlConstantsService
  ) {}

  public getIssues(projectId: string, filters: ErrorIssueFilters = {}): Observable<Paginated<ErrorIssue>> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this._http.get<Paginated<ErrorIssue>>(this.urls.ERROR_ISSUES(projectId), { params });
  }

  public getIssue(projectId: string, issueId: string): Observable<ErrorIssueDetail> {
    return this._http.get<ErrorIssueDetail>(this.urls.ERROR_ISSUE_BY_ID(projectId, issueId));
  }

  public setIssueStatus(
    projectId: string,
    issueId: string,
    status: ErrorIssueStatus
  ): Observable<ErrorIssueDetail> {
    return this._http.patch<ErrorIssueDetail>(this.urls.ERROR_ISSUE_BY_ID(projectId, issueId), { status });
  }

  public deleteIssue(projectId: string, issueId: string): Observable<void> {
    return this._http.delete<void>(this.urls.ERROR_ISSUE_BY_ID(projectId, issueId));
  }

  public getIssueEvents(
    projectId: string,
    issueId: string,
    page = 1
  ): Observable<Paginated<ErrorEventSummary>> {
    const params = new HttpParams().set('page', String(page));
    return this._http.get<Paginated<ErrorEventSummary>>(this.urls.ERROR_ISSUE_EVENTS(projectId, issueId), {
      params,
    });
  }

  public getEvent(projectId: string, issueId: string, eventId: string): Observable<ErrorEvent> {
    return this._http.get<ErrorEvent>(this.urls.ERROR_EVENT_BY_ID(projectId, issueId, eventId));
  }

  public getStats(projectId: string): Observable<ErrorTrackingStats> {
    return this._http.get<ErrorTrackingStats>(this.urls.ERROR_STATS(projectId));
  }

  public getKeys(projectId: string): Observable<ErrorTrackingKey[]> {
    return this._http.get<ErrorTrackingKey[]>(this.urls.ERROR_KEYS(projectId));
  }

  public createKey(projectId: string, data: Partial<ErrorTrackingKey>): Observable<ErrorTrackingKey> {
    return this._http.post<ErrorTrackingKey>(this.urls.ERROR_KEYS(projectId), data);
  }

  public deleteKey(projectId: string, keyId: string): Observable<void> {
    return this._http.delete<void>(this.urls.ERROR_KEY_BY_ID(projectId, keyId));
  }

  public getSourceMaps(projectId: string, release?: string): Observable<SourceMapArtifact[]> {
    let params = new HttpParams();
    if (release) {
      params = params.set('release', release);
    }
    return this._http.get<SourceMapArtifact[]>(this.urls.ERROR_SOURCEMAPS(projectId), { params });
  }

  public uploadSourceMap(projectId: string, release: string, file: File): Observable<SourceMapArtifact> {
    const body = new FormData();
    body.append('release', release);
    body.append('file', file, file.name);

    return this._http.post<SourceMapArtifact>(this.urls.ERROR_SOURCEMAPS(projectId), body);
  }

  public deleteSourceMap(projectId: string, artifactId: string): Observable<void> {
    return this._http.delete<void>(this.urls.ERROR_SOURCEMAP_BY_ID(projectId, artifactId));
  }
}
